<template>
  <div ref="cardRef" class="items-start gap-6 py-3.5 md:py-7 md:flex md:pr-20">
    <NuxtLink :to="toLink" class="block group w-24 h-auto shrink-0 mb-3 md:mb-0" @click="chooseService">
      <img v-if="service.logoUrl" :src="service.logoUrl" :alt="`${service.name} logo`" />
      <div v-else class="flex justify-center items-center">
        <HoHIcon class="size-10 text-gray-200 group-hover:text-gray transition-colors duration-200" />
      </div>
    </NuxtLink>
    <div class="w-full">
      <NuxtLink :to="toLink" class="hover:underline transition-colors duration-200" @click="chooseService">
        <h2 class="text-2xl font-bold mb-4 xl:mb-2 underline lg:no-underline">
          {{ service.name }}
        </h2>
      </NuxtLink>

      <div class="flex flex-col mb-4">
        <!-- Distance and opening times -->
        <div class="mb-3 flex flex-col items-start gap-2 xl:flex-row">
          <FrontendServiceSearchResultDistanceFromService
            v-if="showLocationDistance"
            :distance-in-miles="service.distance.miles"
          />
          <UIcon
            v-if="showLocationDistance"
            name="mdi:checkbox-blank-circle"
            class="size-0.5 mt-2.5 text-gray-700 hidden xl:block"
          />
          <FrontendServiceOpeningTimesState
            :opening-times="service.openingTimes"
            :opening-times-type="service.openingTimesType"
            :completely-irregular-hours-text="service.completelyIrregularHoursText"
          />
        </div>
        <div v-if="service.offersInPersonSupport && service.address_isVisibleToPublic" class="font-light">
          {{ cleanServiceLocationName }}
        </div>
        <div v-else-if="service.offersTelephoneSupport">Telephone or text service</div>
        <div v-else-if="service.offersOnlineSupport">Online service</div>
      </div>

      <!-- Service description -->
      <div class="font-light mb-3">
        <FrontendExpandableTipTap
          :content="service.descriptionHtml"
          :preview-lines="descriptionPreviewLines"
        />
      </div>

      <!-- Service tags -->
      <FrontendServiceTags
        :min-age="service.minAge"
        :max-age="service.maxAge"
        :support-need-category-ids="service.supportNeedCategories"
        :age-verified="service.ageVerified"
        :service="publicService"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ElasticServiceWithDistance } from '~/types/ElasticService'
import { transformElasticServiceIntoPublicService } from '~/utils/transformElasticServiceIntoPublicService'

const props = defineProps({
  service: {
    type: Object as PropType<ElasticServiceWithDistance>,
    required: true
  },
  index: {
    type: Number,
    required: true
  },
  gaEvent: {
    type: String as PropType<'service_search_results_select' | 'service_select_as_next_service' | 'disabled'>,
    required: true
  }
})

const { width: winWidth } = useWindowSize()
const descriptionPreviewLines = computed(() => {
  if (!winWidth.value) return 2
  // we want to show more lines on smaller screens
  return winWidth.value < 640 ? 4 : 2
})

const { filters } = useServiceSearchStore()

const toLink = computed(() => {
  let base = `/service/${props.service.uid}`
  const query = useRoute().query
  if (query?.org_id) base += `?org_id=${query.org_id}`

  return base
})

const showLocationDistance = computed(() => {
  const userSelectedLocation = filters.coordinatesLat && filters.coordinatesLon
  return (props.service.address_line1 || props.service.address_postcode) && userSelectedLocation
})

// fire off the GA event when the user clicks the card
const chooseService = () => {
  if (props.gaEvent === 'disabled') return

  useGaEvent(props.gaEvent, {
    filters: useGaEventHelpers().getGaFiltersObject(),
    index: props.index,
    service: useGaEventHelpers().elasticServiceToGaService(props.service)
  })
}

const cleanServiceLocationName = computed(() => {
  const data = props.service
  // Construct address components
  const addressComponents = [
    data.address_line1,
    data.address_town,
    data.address_county,
    data.address_postcode
  ].filter(Boolean) // Filter out null or empty values

  // Format the address
  return addressComponents.join(', ')
})

// fire off the GA event when the card is in view, only once though
const cardRef = ref<HTMLElement | null>(null)
const hasIntersected = ref(false)
useIntersectionObserver(cardRef, ([{ isIntersecting }]) => {
  if (props.gaEvent === 'disabled') return
  if (isIntersecting && !hasIntersected.value) {
    hasIntersected.value = true

    useGaEvent('service_search_result_impression', {
      filters: useGaEventHelpers().getGaFiltersObject(),
      index: props.index,
      service: useGaEventHelpers().elasticServiceToGaService(props.service)
    })
  }
})

const publicService = computed(() => {
  return transformElasticServiceIntoPublicService(props.service)
})
</script>
